import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Edit,
  Building2,
  MapPin,
  Zap,
  FileText,
  CheckCircle,
  XCircle,
  Users,
  Calendar,
  Hash,
  Activity,
  User,
  Clock,
  Globe,
  Navigation,
  DollarSign,
  Factory,
  Phone,
  Mail,
  Map,
  ShieldCheck,
  Database
} from 'lucide-react';
import backendApi from '../../services/backendApi';
import { useAuth } from '../../context/AuthContext';
import { DEFAULT_MODULE_CATALOG } from '../../utils/moduleAccessAdmin';

interface Site {
  site_id: string;
  site_name: string;
  site_code: string;
  site_type?: string;
  location?: string;
  capacity_mw?: number;
  capacity_mwp?: number;
  is_active: boolean;
  description?: string;
  // Company fields
  company_id?: number | null;
  company_name?: string | null;
  company_code?: string | null;
  database_name?: string | null;
  // New fields
  local_time_zone?: string | null;
  timezone_preference?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  altitude?: number | null;
  ppa_tariff?: number | null;
  plant_code?: number | null;
  currency?: string | null;
  plant_commissioning_date?: string | null;
  data_resolution?: string | null;
  site_manager_name?: string | null;
  site_manager_email?: string | null;
  site_manager_phone_no?: string | null;
  created_at?: string;
  updated_at?: string;
  live_iframe_url?: string | null;
  analytics_iframe_url?: string | null;
}

interface SiteUser {
  id: number;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  is_active?: boolean;
}

interface CompanyOption {
  company_id?: number;
  company_name?: string;
  company_code?: string;
}

const MODULE_NAME_MAP = DEFAULT_MODULE_CATALOG.reduce<Record<string, string>>((acc, item) => {
  acc[item.module_key] = item.module_name;
  return acc;
}, {});

const SiteDetail: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState<Site | null>(null);
  const [siteUsers, setSiteUsers] = useState<SiteUser[]>([]);
  const [userCount, setUserCount] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [assignedModuleKeys, setAssignedModuleKeys] = useState<string[]>([]);
  const [moduleSource, setModuleSource] = useState<'site' | 'company' | 'none'>('none');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchSiteDetails();
      fetchSiteUsers();
    }
  }, [id]);

  const fetchSiteDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await backendApi.getSite(id, true); // Use admin endpoint
      if (response.success && response.site) {
        let resolvedSite = response.site as Site;

        // Fallback: resolve company display data when API returns only company_id.
        if (!resolvedSite.company_name && resolvedSite.company_id) {
          if (
            user?.companyId &&
            Number(user.companyId) === Number(resolvedSite.company_id) &&
            user?.company
          ) {
            resolvedSite = {
              ...resolvedSite,
              company_name: user.company
            };
          } else {
            try {
              const companiesResponse = await backendApi.getCompanies();
              if (companiesResponse.success && Array.isArray(companiesResponse.companies)) {
                const matchedCompany = (companiesResponse.companies as CompanyOption[]).find(
                  (company) => Number(company.company_id) === Number(resolvedSite.company_id)
                );
                if (matchedCompany) {
                  resolvedSite = {
                    ...resolvedSite,
                    company_name: matchedCompany.company_name || resolvedSite.company_name || null,
                    company_code: matchedCompany.company_code || resolvedSite.company_code || null
                  };
                }
              }
            } catch (companyError) {
}
          }
        }

        setSite(resolvedSite);
        void fetchSiteModules(resolvedSite);
      } else {
        setError(response.error || 'Failed to load site');
      }
    } catch (error: any) {
      console.error('Error fetching site:', error);
      setError('Failed to load site details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteModules = async (siteData: Site) => {
    const siteId = Number(siteData.site_id || id);
    if (!siteId) {
      setAssignedModuleKeys([]);
      setModuleSource('none');
      return;
    }

    setLoadingModules(true);
    try {
      const policyResponse = await backendApi.getNavigationRolePolicies({
        siteId,
        scopeLevel: 'site',
      });

      const rolePolicyKeys = new Set<string>();
      if (policyResponse?.success) {
        (policyResponse.allowed_module_keys || []).forEach((key: string) => {
          if (key) rolePolicyKeys.add(String(key).trim());
        });

        (policyResponse.policies || []).forEach((policy: any) => {
          const permissionKey = String(policy?.permission_key || '').trim();
          const effect = String(policy?.effect || '').toLowerCase();
          if (!permissionKey.endsWith('.view') || effect !== 'allow' || !policy?.is_active || policy?.is_valid_now === false) {
            return;
          }
          rolePolicyKeys.add(permissionKey.replace(/\.view$/, ''));
        });
      }

      if (rolePolicyKeys.size > 0) {
        setAssignedModuleKeys(Array.from(rolePolicyKeys).sort());
        setModuleSource('site');
        return;
      }

      if (siteData.company_id) {
        const subscriptionResponse = await backendApi.getNavigationSubscriptions(siteData.company_id);
        if (subscriptionResponse?.success) {
          const companyKeys: string[] = (subscriptionResponse.subscriptions || [])
            .filter((item: any) => item?.is_active_now || String(item?.status || '').toLowerCase() === 'active')
            .map((item: any) => String(item?.module_key || '').trim())
            .filter((key: string): key is string => key.length > 0);

          if (companyKeys.length > 0) {
            setAssignedModuleKeys(Array.from(new Set(companyKeys)).sort());
            setModuleSource('company');
            return;
          }
        }
      }

      setAssignedModuleKeys([]);
      setModuleSource('none');
    } catch (moduleError) {
      console.error('Error loading assigned site modules:', moduleError);
      setAssignedModuleKeys([]);
      setModuleSource('none');
    } finally {
      setLoadingModules(false);
    }
  };

  const fetchSiteUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await backendApi.getSiteUsers(id);
      if (response.success) {
        const users = Array.isArray(response.users) ? response.users : [];
        const count = typeof response.user_count === 'number' ? response.user_count : users.length;
        if (users.length > 0 || count > 0) {
          setSiteUsers(users);
          setUserCount(count);
          return;
        }
      }

      // Fallback for permission-restricted or empty site-user payloads.
      try {
        const usersResponse = await backendApi.getUsers();
        if (usersResponse.success && Array.isArray(usersResponse.users)) {
          const filtered = usersResponse.users.filter((u: any) => {
            const userSiteId = u?.site?.site_id ?? u?.site_id ?? u?.site;
            return String(userSiteId) === String(id);
          });
          setSiteUsers(filtered);
          setUserCount(filtered.length);
        }
      } catch (fallbackError) {
}
    } catch (error: any) {
      console.error('Error fetching site users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-shell">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading site details...</p>
        </div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
        <div className="max-w-5xl mx-auto admin-section">
          <div className="admin-section-body py-16 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#0B1A2F] mb-2">Site Not Found</h2>
            <p className="text-[#5B708B] mb-6">{error || 'The site you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/admin/sites')}
              className="admin-button admin-button-primary mx-auto"
            >
              <ArrowLeft size={16} />
              Back to Sites
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-hero p-4 sm:p-4 lg:p-5"
        >
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">Site profile</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-[linear-gradient(135deg,#0084CF,#1C4D88)] items-center justify-center text-white shadow-[0_12px_20px_rgba(0,132,207,0.2)]">
                  <Building2 size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">{site.site_name}</h1>
                  <p className="admin-page-subtitle mt-2">Code: {site.site_code} • {site.site_type || 'Operational Site'}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/admin/sites')} className="admin-button admin-button-ghost">
                <ArrowLeft size={16} />
                Back to Sites
              </button>
              <button onClick={() => navigate(`/admin/sites/${id}/edit`)} className="admin-button admin-button-primary">
                <Edit size={16} />
                Edit Site
              </button>
            </div>
          </div>

          <div className="admin-stat-strip mt-6">
            <div className="admin-stat-card">
              <p className="admin-stat-label">Status</p>
              <p className="admin-stat-value">{site.is_active ? 'Active' : 'Inactive'}</p>
              <p className="admin-stat-hint">Current operational state</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Assigned Users</p>
              <p className="admin-stat-value">{userCount}</p>
              <p className="admin-stat-hint">Users mapped to this site</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Capacity</p>
              <p className="admin-stat-value">{site.capacity_mw || 0} MW</p>
              <p className="admin-stat-hint">AC capacity • DC: {site.capacity_mwp || 0} MWp</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Location</p>
              <p className="admin-stat-value">{site.location || 'N/A'}</p>
              <p className="admin-stat-hint">Timezone: {site.local_time_zone || site.timezone_preference || 'N/A'}</p>
            </div>
          </div>
        </motion.section>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="admin-section">
              <div className="admin-section-header">
                <div>
                  <h2 className="admin-section-title">Core Site Information</h2>
                  <p className="admin-section-copy">Identity, company mapping, status, and primary operational values.</p>
                </div>
              </div>
              <div className="admin-section-body grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="admin-row"><div className="admin-row-icon"><Hash size={16} /></div><div><p className="admin-meta-label">Site Code</p><p className="admin-meta-value">{site.site_code}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><Activity size={16} /></div><div><p className="admin-meta-label">Site Type</p><p className="admin-meta-value">{site.site_type || 'Not specified'}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><Building2 size={16} /></div><div><p className="admin-meta-label">Company</p><p className="admin-meta-value">{site.company_name || (site.company_id ? `ID ${site.company_id}` : 'Not assigned')}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><Hash size={16} /></div><div><p className="admin-meta-label">Company Code</p><p className="admin-meta-value">{site.company_code || 'N/A'}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><MapPin size={16} /></div><div><p className="admin-meta-label">Location</p><p className="admin-meta-value">{site.location || 'N/A'}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon">{site.is_active ? <CheckCircle size={16} /> : <XCircle size={16} />}</div><div><p className="admin-meta-label">Status</p><p className="admin-meta-value">{site.is_active ? 'Active' : 'Inactive'}</p></div></div>
              </div>
            </motion.section>

            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="admin-section">
              <div className="admin-section-header">
                <div>
                  <h2 className="admin-section-title">Technical Details</h2>
                  <p className="admin-section-copy">Timezone, coordinates, plant configuration and commercial data.</p>
                </div>
              </div>
              <div className="admin-section-body grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="admin-row"><div className="admin-row-icon"><Clock size={16} /></div><div><p className="admin-meta-label">Local Time Zone</p><p className="admin-meta-value">{site.local_time_zone || 'N/A'}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><Globe size={16} /></div><div><p className="admin-meta-label">Timezone Preference</p><p className="admin-meta-value">{site.timezone_preference || 'N/A'}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><Map size={16} /></div><div><p className="admin-meta-label">Latitude</p><p className="admin-meta-value">{site.latitude ?? 'N/A'}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><Navigation size={16} /></div><div><p className="admin-meta-label">Longitude</p><p className="admin-meta-value">{site.longitude ?? 'N/A'}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><Navigation size={16} /></div><div><p className="admin-meta-label">Altitude</p><p className="admin-meta-value">{site.altitude ?? 'N/A'}{site.altitude !== null && site.altitude !== undefined ? ' m' : ''}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><Zap size={16} /></div><div><p className="admin-meta-label">Capacity</p><p className="admin-meta-value">{site.capacity_mw || 0} MW • {site.capacity_mwp || 0} MWp</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><Factory size={16} /></div><div><p className="admin-meta-label">Plant Code</p><p className="admin-meta-value">{site.plant_code ?? 'N/A'}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><DollarSign size={16} /></div><div><p className="admin-meta-label">PPA Tariff</p><p className="admin-meta-value">{site.ppa_tariff !== null && site.ppa_tariff !== undefined ? `${site.currency || 'INR'} ${typeof site.ppa_tariff === 'number' ? site.ppa_tariff.toFixed(4) : parseFloat(String(site.ppa_tariff || 0)).toFixed(4)}` : 'N/A'}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><Calendar size={16} /></div><div><p className="admin-meta-label">Commissioning Date</p><p className="admin-meta-value">{site.plant_commissioning_date ? new Date(site.plant_commissioning_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</p></div></div>
                <div className="admin-row"><div className="admin-row-icon"><Activity size={16} /></div><div><p className="admin-meta-label">Data Resolution</p><p className="admin-meta-value">{site.data_resolution || 'N/A'}</p></div></div>
              </div>
            </motion.section>

            {/* Site Telemetry Database */}
            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 }} className="admin-section">
              <div className="admin-section-header">
                <div>
                  <h2 className="admin-section-title">Site Telemetry Database</h2>
                  <p className="admin-section-copy">SQL Server database mapping and telemetry table schema status.</p>
                </div>
              </div>
              <div className="admin-section-body space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-[#F2F8FC]/70 border border-[#C2DFF1]/80 rounded-xl gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0066A3] text-white flex items-center justify-center font-black flex-shrink-0 shadow-xs">
                      <Database size={20} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#1C4D88] uppercase tracking-wide">Telemetry Database Name</p>
                      <p className="text-base font-extrabold text-slate-900 font-mono">
                        {site.database_name || 'Not Configured (Auto-Fallback)'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/admin/sites/${id}/edit`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-white text-[#1C4D88] hover:bg-[#E3F0F9] border border-[#8AC4E6] shadow-2xs transition-colors"
                  >
                    <Edit size={14} />
                    Change Database
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-xs font-bold text-slate-700 mb-1">Telemetry Table 1: Plant & Daily</p>
                    <p className="font-mono text-xs text-slate-900 font-bold">[dbo].[axonmetric_livedaycons]</p>
                    <p className="text-[11px] text-slate-500 mt-1">96 columns • Clustered index on Datetime</p>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-xs font-bold text-slate-700 mb-1">Telemetry Table 2: String Inverters</p>
                    <p className="font-mono text-xs text-slate-900 font-bold">[dbo].[axonmetric_livestringinv]</p>
                    <p className="text-[11px] text-slate-500 mt-1">59 columns • Clustered index on Datetime</p>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Dashboard Embed Configurations */}
            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="admin-section">
              <div className="admin-section-header">
                <div>
                  <h2 className="admin-section-title">Dashboard Embed Configurations</h2>
                  <p className="admin-section-copy">Dynamic iframe tabs configured for this site's user dashboard.</p>
                </div>
              </div>
              <div className="admin-section-body space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Live Embeds */}
                  <div className="space-y-3">
                    <h3 className="text-base font-extrabold text-gray-900 border-b pb-1.5 flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
                      Live Dashboards
                    </h3>
                    {(() => {
                      if (!site.live_iframe_url) {
                        return <p className="text-xs text-gray-500 italic py-2">No live dashboards configured.</p>;
                      }
                      try {
                        const parsed = JSON.parse(site.live_iframe_url);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                          return (
                            <div className="space-y-2">
                              {parsed.map((item: any, idx: number) => (
                                <div key={idx} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                  <p className="text-sm font-extrabold text-gray-900 mb-1">{item.name || `Live Dashboard ${idx + 1}`}</p>
                                  <p className="text-xs text-[#1C4D88] font-bold truncate font-mono select-all bg-white p-1.5 border rounded" title={item.url}>
                                    {item.url}
                                  </p>
                                </div>
                              ))}
                            </div>
                          );
                        }
                      } catch (e) {}
                      return (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-sm font-extrabold text-gray-900 mb-1">Live Dashboard (Legacy)</p>
                          <p className="text-xs text-[#1C4D88] font-bold truncate font-mono select-all bg-white p-1.5 border rounded" title={site.live_iframe_url}>
                            {site.live_iframe_url}
                          </p>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Performance Analytics Embeds */}
                  <div className="space-y-3">
                    <h3 className="text-base font-extrabold text-gray-900 border-b pb-1.5 flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#0084CF]"></span>
                      Performance Analytics Dashboards
                    </h3>
                    {(() => {
                      if (!site.analytics_iframe_url) {
                        return <p className="text-xs text-gray-500 italic py-2">No analytics dashboards configured.</p>;
                      }
                      try {
                        const parsed = JSON.parse(site.analytics_iframe_url);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                          return (
                            <div className="space-y-2">
                              {parsed.map((item: any, idx: number) => (
                                <div key={idx} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                  <p className="text-sm font-extrabold text-gray-900 mb-1">{item.name || `Analytics Dashboard ${idx + 1}`}</p>
                                  <p className="text-xs text-[#1C4D88] font-bold truncate font-mono select-all bg-white p-1.5 border rounded" title={item.url}>
                                    {item.url}
                                  </p>
                                </div>
                              ))}
                            </div>
                          );
                        }
                      } catch (e) {}
                      return (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-sm font-extrabold text-gray-900 mb-1">Analytics Dashboard (Legacy)</p>
                          <p className="text-xs text-[#1C4D88] font-bold truncate font-mono select-all bg-white p-1.5 border rounded" title={site.analytics_iframe_url}>
                            {site.analytics_iframe_url}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </motion.section>

            {(site.site_manager_name || site.site_manager_email || site.site_manager_phone_no || site.description) && (
              <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="admin-section">
                <div className="admin-section-header">
                  <div>
                    <h2 className="admin-section-title">Manager & Notes</h2>
                    <p className="admin-section-copy">Manager contact information and descriptive notes for this site.</p>
                  </div>
                </div>
                <div className="admin-section-body space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-xl border border-gray-200 bg-white p-3 flex items-start gap-3 min-w-0">
                      <div className="admin-row-icon shrink-0"><User size={16} /></div>
                      <div className="min-w-0">
                        <p className="admin-meta-label">Manager Name</p>
                        <p className="admin-meta-value break-words">{site.site_manager_name || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-3 flex items-start gap-3 min-w-0">
                      <div className="admin-row-icon shrink-0"><Mail size={16} /></div>
                      <div className="min-w-0">
                        <p className="admin-meta-label">Manager Email</p>
                        <p className="admin-meta-value break-all">{site.site_manager_email || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-3 flex items-start gap-3 min-w-0">
                      <div className="admin-row-icon shrink-0"><Phone size={16} /></div>
                      <div className="min-w-0">
                        <p className="admin-meta-label">Manager Phone</p>
                        <p className="admin-meta-value break-all">{site.site_manager_phone_no || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  {site.description && (
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <p className="text-sm font-semibold text-[#0B1A2F] mb-2">Description</p>
                      <p className="text-sm text-[#4A5F7A] whitespace-pre-wrap">{site.description}</p>
                    </div>
                  )}
                </div>
              </motion.section>
            )}
          </div>

          <div className="space-y-6">
            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="admin-section">
              <div className="admin-section-header">
                <div>
                  <h2 className="admin-section-title">Assigned Users</h2>
                  <p className="admin-section-copy">Users currently mapped to this site context.</p>
                </div>
              </div>
              <div className="admin-section-body">
                {loadingUsers ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-sm text-gray-500">Loading users...</p>
                  </div>
                ) : siteUsers.length > 0 ? (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-[#E3F0F9] bg-[#F2F8FC] px-3 py-2">
                      <p className="text-xs text-[#1C4D88] font-semibold">Total Users</p>
                      <p className="text-2xl font-bold text-[#0084CF]">{userCount}</p>
                    </div>
                    <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
                      {siteUsers.map((mappedUser) => (
                        <button
                          key={mappedUser.id}
                          onClick={() => navigate(`/admin/users/${mappedUser.id}`)}
                          className="w-full text-left rounded-lg border border-gray-200 bg-white px-3 py-3 hover:bg-gray-50"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-[#0B1A2F] truncate">
                                {mappedUser.first_name && mappedUser.last_name ? `${mappedUser.first_name} ${mappedUser.last_name}` : mappedUser.username}
                              </p>
                              <p className="text-xs text-[#5B708B] truncate">{mappedUser.email || mappedUser.username}</p>
                              {mappedUser.role && <p className="text-[11px] text-[#0084CF] mt-1">{mappedUser.role}</p>}
                            </div>
                            {mappedUser.is_active !== false ? <CheckCircle size={15} className="text-green-600" /> : <XCircle size={15} className="text-red-600" />}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="admin-empty">No users assigned to this site.</div>
                )}
              </div>
            </motion.section>

            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }} className="admin-section">
              <div className="admin-section-header">
                <div>
                  <h2 className="admin-section-title">Assigned Modules</h2>
                  <p className="admin-section-copy">
                    {moduleSource === 'site' && 'Loaded from site-level role policies.'}
                    {moduleSource === 'company' && 'No explicit site policy found; showing company active subscriptions.'}
                    {moduleSource === 'none' && 'No module assignments available for this site yet.'}
                  </p>
                </div>
              </div>
              <div className="admin-section-body">
                {loadingModules ? (
                  <div className="text-center py-7">
                    <div className="w-7 h-7 border-2 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-sm text-gray-500">Loading module access...</p>
                  </div>
                ) : assignedModuleKeys.length > 0 ? (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-[#E3F0F9] bg-[#F2F8FC] px-3 py-2">
                      <p className="text-xs text-[#1C4D88] font-semibold">Modules available</p>
                      <p className="text-xl font-bold text-[#0084CF]">{assignedModuleKeys.length}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {assignedModuleKeys.map((moduleKey) => (
                        <span key={moduleKey} className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F4FF] text-[#0B4F82] px-2.5 py-1 text-xs font-bold border border-[#CFE8FF]">
                          <ShieldCheck size={12} />
                          {MODULE_NAME_MAP[moduleKey] || moduleKey}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="admin-empty !py-8">No modules assigned for this site.</div>
                )}
              </div>
            </motion.section>

            <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="admin-section">
              <div className="admin-section-header">
                <div>
                  <h2 className="admin-section-title">Timeline</h2>
                  <p className="admin-section-copy">Creation and last update metadata.</p>
                </div>
              </div>
              <div className="admin-section-body space-y-3">
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <p className="text-[11px] font-semibold text-[#5B708B]">Created At</p>
                  <p className="text-sm font-medium text-[#0B1A2F]">{formatDate(site.created_at)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <p className="text-[11px] font-semibold text-[#5B708B]">Last Updated</p>
                  <p className="text-sm font-medium text-[#0B1A2F]">{formatDate(site.updated_at)}</p>
                </div>
              </div>
            </motion.section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteDetail;


